import { useEffect, useState, useCallback, useRef } from 'react';
import { useBeforeUnload } from 'react-router-dom';

export function useDirtyState(pristineData) {
    const pristineRef = useRef(pristineData);
    const [isDirty, setIsDirty] = useState(false);
    const [currentData, setCurrentData] = useState(pristineData);
    const [showDialog, setShowDialog] = useState(false);

    useEffect(() => {
        const pristine = JSON.stringify(pristineRef.current);
        const current = JSON.stringify(currentData);
        setIsDirty(pristine !== current);
    }, [currentData]);

    const markDirty = useCallback((newData) => {
        setCurrentData(newData);
    }, []);

    const resetDirty = useCallback((newPristine) => {
        if (newPristine !== undefined) {
            pristineRef.current = newPristine;
            setCurrentData(newPristine);
        } else {
            pristineRef.current = currentData;
        }
        setIsDirty(false);
        setShowDialog(false);
    }, [currentData]);

    // Browser close / refresh guard
    useBeforeUnload(
        useCallback((event) => {
            if (isDirty) {
                event.preventDefault();
                event.returnValue = '';
            }
        }, [isDirty])
    );

    // Browser back/forward button guard (best-effort for BrowserRouter)
    useEffect(() => {
        if (!isDirty) return;
        const handlePopState = () => {
            const allow = window.confirm('Modifications non enregistrées. Voulez-vous vraiment quitter cette page ?');
            if (!allow) {
                window.history.pushState(null, '', window.location.pathname + window.location.search);
            }
        };
        window.history.pushState(null, '', window.location.pathname + window.location.search);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isDirty]);

    // Manual in-app guard helpers (e.g. Dialog close interception)
    // useBlocker is unavailable in BrowserRouter (non-data router) so this
    // hook relies on beforeunload and manual triggers.
    const confirmNavigation = useCallback(() => {
        setShowDialog(false);
    }, []);

    const cancelNavigation = useCallback(() => {
        setShowDialog(false);
    }, []);

    return { isDirty, markDirty, resetDirty, showDialog, confirmNavigation, cancelNavigation };
}
